/* Pawsy UI wiring — one nav drawer and one cart drawer across the whole store.
 *
 * Background: the two page families were captured from different themes, so they
 * shipped different nav drawers and different cart drawers. patch-ui.mjs strips
 * the captured drawers but leaves each page's own hamburger and cart buttons
 * where they are; this file binds whatever buttons a page has to the single
 * canonical drawer rendered from the layout.
 */
(function () {
  'use strict';

  var NAV_ID = 'Details-pawsy-nav';

  /* ---------------------------------------------------------------- nav --- */
  // The inherited CSS opens the drawer on:
  //   .js details[open].menu-opening > .menu-drawer { transform: translate(0) }
  // so both `open` and `.menu-opening` have to be set, and .menu-drawer must
  // stay a direct child of the <details>.
  function navEl() { return document.getElementById(NAV_ID); }

  function openNav() {
    var d = navEl();
    if (!d) return;
    d.setAttribute('open', '');
    // next frame, so the transition actually runs instead of snapping
    requestAnimationFrame(function () {
      d.classList.add('menu-opening');
      syncScrollLock();
      var first = d.querySelector('.menu-drawer__menu-item');
      if (first) first.focus({ preventScroll: true });
    });
  }

  function closeNav() {
    var d = navEl();
    if (!d || !d.hasAttribute('open')) return;
    d.classList.remove('menu-opening');
    syncScrollLock();
    var drawer = d.querySelector('.menu-drawer');
    var done = function () { d.removeAttribute('open'); syncScrollLock(); };
    if (drawer) {
      var fired = false;
      drawer.addEventListener('transitionend', function h() {
        fired = true; drawer.removeEventListener('transitionend', h); done();
      });
      setTimeout(function () { if (!fired) done(); }, 500);
    } else done();
  }

  function isNavTrigger(el) {
    return el.closest(
      'summary.header__icon--menu, .header__icon--menu, .js-drawer-open-nav,'
      + ' [data-pawsy-nav-open], .site-nav__link--icon.js-drawer-open-nav'
    );
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!(t instanceof Element)) return;

    // close first: the close control sits inside the drawer
    if (t.closest('.pawsy-nav-drawer__close, .menu-drawer-close')) {
      e.preventDefault(); closeNav(); return;
    }
    if (t.closest('#' + NAV_ID + ' .menu-drawer__menu-item')) return; // let links navigate

    var trigger = isNavTrigger(t);
    if (trigger && !trigger.closest('#' + NAV_ID)) {
      e.preventDefault();
      e.stopPropagation();
      // a captured <details> hamburger would otherwise toggle its own empty shell
      var ownDetails = trigger.closest('details');
      if (ownDetails && ownDetails.id !== NAV_ID) ownDetails.removeAttribute('open');
      var d = navEl();
      if (d && d.hasAttribute('open')) closeNav(); else openNav();
      return;
    }

    // click outside the panel closes it
    var nav = navEl();
    if (nav && nav.hasAttribute('open') && !t.closest('#pawsy-menu-drawer')) closeNav();
  }, true);

  document.addEventListener('keyup', function (e) {
    if (e.key === 'Escape' || e.code === 'Escape') closeNav();
  });

  /* --------------------------------------------------------------- cart --- */
  // The captured cart JS binds only `#cart-icon-bubble`, which exists on the
  // PDPs. The homepage header has its own cart link, so bind anything that
  // points at /cart to the same <cart-drawer>.
  // The drawer is class-driven (.animate.active + body.overflow-hidden). Use the
  // custom element's own methods when the theme JS has upgraded it, and fall
  // back to setting those classes directly when it has not — otherwise the
  // drawer would render but never open.
  function cartEl() { return document.querySelector('cart-drawer'); }

  // Single source of truth for the scroll lock.
  //
  // The theme's CartDrawer.close() is:
  //     this.classList.remove('active'),
  //     removeTrapFocus(this.activeElement),
  //     document.body.classList.remove('overflow-hidden')
  //
  // removeTrapFocus throws in this theme
  // ("Cannot access 'trapFocusHandlers' before initialization"), so the third
  // statement never runs: the drawer closes but body keeps overflow-hidden and
  // the page is stuck at the scroll position the drawer opened at, until a
  // reload. Rather than depend on that call chain completing, derive the lock
  // from what is actually open.
  function syncScrollLock() {
    var navOpen = !!document.querySelector('#' + NAV_ID + '.menu-opening');
    var cartOpen = !!document.querySelector('cart-drawer.active');
    document.body.classList.toggle('overflow-hidden', navOpen || cartOpen);
  }

  function openCart(trigger) {
    var d = cartEl();
    if (!d) return false;
    try {
      if (typeof d.open === 'function') d.open(trigger);
      else d.classList.add('animate', 'active');
    } catch (e) {
      d.classList.add('animate', 'active');
    }
    setTimeout(syncScrollLock, 0);   // the theme's open() sets .active async
    return true;
  }

  function closeCart() {
    var d = cartEl();
    if (d) {
      try {
        if (typeof d.close === 'function') d.close();
        else d.classList.remove('active');
      } catch (e) {
        d.classList.remove('active');
      }
    }
    syncScrollLock();
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!(t instanceof Element)) return;

    if (t.closest('[data-pawsy-cart-close], #CartDrawer-Overlay')) {
      e.preventDefault(); closeCart(); return;
    }

    var link = t.closest('a[href$="/cart"], a[href*="/cart?"], .js-drawer-open-cart, [data-pawsy-cart-open]');
    if (!link) return;
    if (link.id === 'cart-icon-bubble' && typeof cartEl()?.open === 'function') return; // theme JS owns it
    if (link.closest('cart-drawer')) return;
    if (openCart(link)) e.preventDefault();   // no drawer at all: let /cart load
  }, true);

  document.addEventListener('keyup', function (e) {
    if (e.key === 'Escape' || e.code === 'Escape') closeCart();
  });

  /* ------------------------------------------------- order-within timer --- */
  // Same rule as the PDP's DeliveryTimer: cutoff at 12:00 UTC, before it you are
  // in `before_noon` and it ships today, after it you roll to tomorrow. The PDP
  // script only toggles the class on the FIRST .delivery-timer, so the cart's
  // card is driven here instead — and this also works on pages where that
  // script is not loaded at all.
  function pad(n) { return n < 10 ? '0' + n : String(n); }

  function tickTimers() {
    var cards = document.querySelectorAll('[data-pawsy-timer]');
    if (!cards.length) return;
    var now = new Date();
    var target = new Date(now);
    target.setUTCHours(12, 0, 0, 0);
    var afterCutoff = now.getUTCHours() >= 12;
    if (afterCutoff) target.setUTCDate(target.getUTCDate() + 1);

    var diff = target.getTime() - now.getTime();
    var text = pad(Math.floor(diff / 3600000)) + ' : '
      + pad(Math.floor((diff % 3600000) / 60000)) + ' : '
      + pad(Math.floor((diff % 60000) / 1000));

    cards.forEach(function (card) {
      card.classList.toggle('after_noon', afterCutoff);
      card.classList.toggle('before_noon', !afterCutoff);
      card.querySelectorAll('.timer').forEach(function (t) {
        // textContent, and only when it actually changed. Writing every tick
        // mutates the DOM needlessly and — see below — used to feed a loop.
        if (t.textContent !== text) t.textContent = text;
      });
    });
  }

  tickTimers();
  // Polling alone is enough: this re-queries the DOM each tick, so a drawer that
  // was re-rendered picks the countdown back up within a second.
  //
  // There was a MutationObserver here watching #CartDrawer with subtree:true to
  // re-tick after a re-render. That was an infinite loop: tickTimers writes into
  // .timer, .timer is inside #CartDrawer, the write is a mutation, the observer
  // fires, it writes again. MutationObserver callbacks are microtasks, so the
  // queue never drained and the main thread locked up permanently — but only
  // once the cart had an item (that is when the timer card exists), and the cart
  // cookie made it survive reloads. Do not reintroduce an observer here.
  setInterval(tickTimers, 1000);
})();
